import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import adminApiService from '../../services/adminApiService';

const AdminPaymentRefundInfoDetails = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refundDetails, setRefundDetails] = useState(null);

  useEffect(() => {
    if (agentId) {
      loadRefundDetails();
    }
  }, [agentId]);

  const loadRefundDetails = async () => {
    try {
      setLoading(true);
      console.log('💰 Loading payment refund details for Agent ID:', agentId);
      const response = await adminApiService.getPaymentRefundDetails(agentId);
      console.log('📊 Payment Refund Details API Response:', response);
      
      if (response.success) {
        setRefundDetails(response.data);
        console.log('✅ Payment refund details loaded successfully');
      } else {
        console.error('❌ Payment Refund Details API returned error:', response.message);
        toast.error(response.message || 'Failed to load refund details');
        navigate('/admin/dashboard/payment-refund-info');
      }
    } catch (error) {
      console.error('❌ Payment refund details load error:', error);
      toast.error('Failed to load refund details');
      navigate('/admin/dashboard/payment-refund-info');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    } catch (error) {
      return amount;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'COMPLETED': 'bg-green-100 text-green-800',
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'FAILED': 'bg-red-100 text-red-800',
      'PROCESSING': 'bg-blue-100 text-blue-800'
    };

    const colorClass = statusColors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status || 'PENDING'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!refundDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Refund Details Not Found</h2>
          <p className="text-gray-600 mb-4">The requested refund details could not be found.</p>
          <button
            onClick={() => navigate('/admin/dashboard/payment-refund-info')}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
          >
            Back to Refund Information
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment Refund Details</h1>
              <p className="text-gray-600">View detailed refund transaction information</p>
            </div>
            <button
              onClick={() => navigate('/admin/dashboard/payment-refund-info')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Back to Refund Information
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Refund Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Refund Details */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund ID</label>
                    <input
                      type="text"
                      value={refundDetails.refundId || refundDetails.agentId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID</label>
                    <input
                      type="text"
                      value={refundDetails.transactionId || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Amount</label>
                    <input
                      type="text"
                      value={formatCurrency(refundDetails.refundAmount)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method</label>
                    <input
                      type="text"
                      value={refundDetails.modeOfPayment || 'Online'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Status</label>
                    <input
                      type="text"
                      value={refundDetails.refundStatus || 'Completed'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refund Date</label>
                    <input
                      type="text"
                      value={formatDate(refundDetails.refundDate)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  {/* Customer Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={refundDetails.agentName || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
                    <input
                      type="text"
                      value={refundDetails.agentEmail || '-'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <input
                      type="text"
                      value={refundDetails.paymentStatus || 'Refunded'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processing Fee</label>
                    <input
                      type="text"
                      value={formatCurrency(refundDetails.processingFee || 0)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Refunded By</label>
                    <input
                      type="text"
                      value={refundDetails.refundedBy || 'System'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Gateway</label>
                    <input
                      type="text"
                      value={refundDetails.paymentGateway || 'Default Gateway'}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
                    />
                  </div>
                </div>

                {/* Refund Reason */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refund Reason</label>
                  <textarea
                    value={refundDetails.refundReason || 'Customer requested refund'}
                    readOnly
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 resize-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Refund Status</h3>
              </div>
              <div className="p-6">
                <div className="flex justify-center">
                  {getStatusBadge(refundDetails.refundStatus || 'Completed')}
                </div>
              </div>
            </div>

            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Refund Summary</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Original Amount</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(refundDetails.originalAmount || refundDetails.refundAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Processing Fee</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(refundDetails.processingFee || 0)}
                  </span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-900">Refund Amount</span>
                    <span className="text-sm font-bold text-green-600">
                      {formatCurrency(refundDetails.refundAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Quick Info</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Refund Method</span>
                  <span className="text-sm font-medium text-gray-900">
                    {refundDetails.modeOfPayment || 'Online'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Processing Time</span>
                  <span className="text-sm font-medium text-gray-900">3-5 Business Days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Refund Type</span>
                  <span className="text-sm font-medium text-gray-900">Full Refund</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentRefundInfoDetails;